import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import EcosystemCard from "@/components/landing/EcosystemCard";
import ContactForm from "@/components/landing/ContactForm";
import {
  ArrowRight,
  Zap,
  Shield,
  TrendingUp,
  Globe,
  Coins,
  Award,
  Radar,
  Search,
} from "lucide-react";

// --- Use Environment Variables for URLs ---
const DEX_URL = import.meta.env.VITE_DEX_URL;
const MINER_URL = import.meta.env.VITE_MINER_URL;
const TRADE_LAB_URL =
  import.meta.env.VITE_TRADE_LAB_URL || "https://tradelab.pilumfi.com";

// Update this to the production URL where this page will be hosted.
const CANONICAL_URL = "https://pilumfi.com";

// --- Structured Data for Search Engines & AI (JSON-LD) ---
const organizationData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Pilum Finance",
  url: "https://pilumfi.com",
  logo: "https://pilumfi.com/logo.webp",
  description:
    "Pilum Finance is a secure DeFi ecosystem: a DEX aggregator, the Pilum Miner app, and the Pilum Trade Lab crypto opportunity terminal.",
  sameAs: [
    "https://x.com/pilumfi",
    "https://www.facebook.com/pilumfinance",
    "https://www.instagram.com/pilumfi",
  ],
};

const ecosystemData = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Pilum Finance Ecosystem",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Pilum DEX Aggregator",
      url: "https://dex.pilumfi.com",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Pilum Miner",
      url: "https://miner.pilumfi.com",
    },
    {
      "@type": "ListItem",
      position: 3,
      name: "Pilum Trade Lab",
      url: "https://tradelab.pilumfi.com",
    },
  ],
};

export default function LandingPage() {
  // Scroll to hash targets (e.g. #contact) when arriving from another page
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        setTimeout(() => target.scrollIntoView({ behavior: "smooth" }), 100);
      }
    }
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* ================= SEO / AI / SOCIAL META DATA ================= */}
      <Helmet>
        <title>
          Pilum Finance — DeFi Ecosystem, DEX Aggregator & Crypto Tools
        </title>
        <meta
          name="description"
          content="Pilum Finance is a secure DeFi ecosystem. Swap tokens through our DEX aggregator, mine points with Pilum Miner, and discover opportunities with Pilum Trade Lab — no login required."
        />
        <meta
          name="keywords"
          content="Pilum Finance, DEX aggregator, DeFi, crypto swap, multi-hop routing, multi-chain, testnet, Pilum Miner, Pilum Trade Lab, crypto opportunity, Monad, Citrea, Rise, Sepolia"
        />
        <meta name="author" content="Pilum Finance" />
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <meta name="theme-color" content="#087f5b" />

        {/* Canonical */}
        <link rel="canonical" href={CANONICAL_URL} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Pilum Finance" />
        <meta
          property="og:title"
          content="Pilum Finance — DeFi Ecosystem, DEX Aggregator & Crypto Tools"
        />
        <meta
          property="og:description"
          content="Swap tokens through our DEX aggregator, mine points with Pilum Miner, and discover opportunities with Pilum Trade Lab."
        />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:image" content="https://pilumfi.com/logo.webp" />
        <meta property="og:image:alt" content="Pilum Finance Logo" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@PilumFinance" />
        <meta
          name="twitter:title"
          content="Pilum Finance — DeFi Ecosystem, DEX Aggregator & Crypto Tools"
        />
        <meta
          name="twitter:description"
          content="Swap tokens through our DEX aggregator, mine points with Pilum Miner, and discover opportunities with Pilum Trade Lab."
        />
        <meta name="twitter:image" content="https://pilumfi.com/logo.webp" />

        {/* Structured data for search engines & AI */}
        <script type="application/ld+json">
          {JSON.stringify(organizationData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(ecosystemData)}
        </script>
      </Helmet>

      <Header />

      {/* Main Content - Centered between header and footer */}
      <main className="flex-1 w-full pt-24 pb-12">
        <div className="container mx-auto px-4">
          {/* Trade Lab Announcement Banner */}
          <section className="mb-12 animate-fade-up">
            <a
              href={TRADE_LAB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden flex flex-col sm:flex-row items-center justify-between gap-4 px-6 sm:px-8 py-5 rounded-2xl bg-gradient-to-r from-primary/15 via-primary/10 to-primary/5 border border-primary/25 shadow-lg hover:shadow-xl hover:border-primary/40 transition-all duration-300 hover:-translate-y-0.5"
            >
              {/* Animated radar ping on the left */}
              <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-primary/15 text-primary relative flex-shrink-0">
                <Radar className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping"></span>
              </div>

              <div className="flex-1 text-center sm:text-left">
                <span className="inline-block text-xs font-bold uppercase tracking-wider text-primary mb-1">
                  New · Ecosystem Product
                </span>
                <h2 className="text-lg sm:text-xl font-bold text-text">
                  Pilum Trade Lab — Adaptive Crypto Opportunity Intelligence
                </h2>
                <p className="text-sm text-text-secondary mt-1">
                  No-login crypto opportunity terminal. Whole-market radar,
                  Predictor candidates &amp; Eagle Watch monitoring.
                </p>
              </div>

              <span className="relative overflow-hidden inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-bold transition-all duration-300 ease-out focus:outline-none bg-primary text-text shadow-lg hover:shadow-xl hover:bg-primary-hover hover:scale-105 transform border border-primary-hover whitespace-nowrap flex-shrink-0">
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                <span className="relative z-10 flex items-center">
                  Explore Trade Lab
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </span>
            </a>
          </section>

          {/* Hero Section */}
          <section className="text-center mb-20">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8 animate-fade-up">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">
                Secure DeFi Ecosystem
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 animate-fade-up">
              <span className="bg-gradient-to-r from-primary via-primary-hover to-primary bg-clip-text text-transparent">
                Pilum Finance
              </span>
              <br />
              <span className="text-2xl sm:text-3xl lg:text-4xl text-text/80 font-light">
                Next Generation DeFi Platform
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto mb-12 animate-fade-up animate-delay-200 leading-relaxed">
              Experience the future of decentralized finance with our
              comprehensive ecosystem. Swap tokens, earn rewards, and build
              wealth in a secure, user-friendly environment.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-up animate-delay-400">
              <a
                href={DEX_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="relative overflow-hidden inline-flex items-center justify-center rounded-xl px-8 py-4 text-lg font-semibold transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 bg-primary text-text shadow-lg hover:shadow-xl hover:bg-primary-hover hover:scale-105 focus:ring-primary transform border border-primary-hover group"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
                <span className="relative z-10 flex items-center">
                  Launch DEX
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </a>
              <a
                href={MINER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-xl px-8 py-4 text-lg font-semibold transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 bg-card text-text shadow-md ring-1 ring-border hover:bg-input hover:shadow-lg hover:scale-105 focus:ring-primary transform group"
              >
                <span className="relative z-10 flex items-center">
                  Start Mining
                  <Zap className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform duration-300" />
                </span>
              </a>
            </div>
          </section>

          {/* Features Section */}
          <section
            id="features"
            className="py-16 bg-gradient-to-b from-transparent to-input rounded-3xl mb-20 scroll-mt-24"
          >
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl font-bold text-text mb-4">
                  Why Choose Pilum Finance?
                </h2>
                <p className="text-text-secondary text-lg max-w-2xl mx-auto">
                  Our ecosystem is built on cutting-edge technology with user
                  experience at its core
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: TrendingUp,
                    title: "High Performance",
                    description:
                      "Lightning-fast transactions with minimal fees and maximum efficiency for all your DeFi needs.",
                  },
                  {
                    icon: Search,
                    title: "Best Price Discovery",
                    description:
                      "Our DEX aggregator scans multiple decentralized exchanges to find the most favorable swap rates for your trades — with multi-hop routing for pairs without direct liquidity pools.",
                  },
                  {
                    icon: Globe,
                    title: "Global Access",
                    description:
                      "Access our platform from anywhere in the world with seamless cross-chain compatibility.",
                  },
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="group p-8 rounded-2xl bg-card border border-border transition-all duration-500 hover:shadow-xl hover:border-primary/30 hover:transform hover:-translate-y-2"
                  >
                    <div className="p-3 rounded-xl bg-primary/10 text-primary w-fit transition-all duration-300 group-hover:scale-110 group-hover:bg-primary/20 mb-6">
                      <feature.icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-semibold text-text mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-text-secondary leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Ecosystem Section */}
          <section id="ecosystem" className="py-16 mb-20 scroll-mt-24">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl font-bold text-text mb-4">
                  Our Ecosystem
                </h2>
                <p className="text-text-secondary text-lg max-w-2xl mx-auto">
                  Discover the powerful tools and platforms that make up the
                  Pilum Finance ecosystem
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                <EcosystemCard
                  title="Pilum DEX Aggregator"
                  description="Aggregates liquidity from multiple DEXs to find the best swap rates with multi-hop routing. No custom contracts — swaps route directly through established on-chain liquidity protocols. Currently live on multiple testnet chains including Monad, Citrea, Rise, and Sepolia."
                  link={DEX_URL}
                  icon={<Coins className="w-8 h-8" />}
                  features={[
                    "Best Price Discovery",
                    "Multi-Hop Routing",
                    "Aggregated Liquidity",
                    "Wallet Connect",
                  ]}
                />
                <EcosystemCard
                  title="Pilum Miner"
                  description="Our simulated miner lets you mine points by participating in the ecosystem. These points will be converted to token rewards in the future."
                  link={MINER_URL}
                  icon={<Award className="w-8 h-8" />}
                  features={[
                    "Simulated Mining",
                    "Earn Points",
                    "Future Airdrop",
                    "No Cost",
                  ]}
                />
                <EcosystemCard
                  title="Pilum Trade Lab"
                  description="Adaptive crypto opportunity intelligence. A no-login, browser-first terminal with whole-market radar, deep-validated Predictor candidates, Eagle Watch monitoring, and candle & coin research."
                  link={TRADE_LAB_URL}
                  icon={<Radar className="w-8 h-8" />}
                  features={[
                    "Market Radar",
                    "Predictor Candidates",
                    "Eagle Watch",
                    "No Login",
                  ]}
                />
              </div>
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="py-16 bg-gradient-to-r from-primary/10 to-primary/5 rounded-3xl mb-20">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-text mb-6">
                Ready to Get Started?
              </h2>
              <p className="text-text-secondary text-lg mb-8 max-w-2xl mx-auto">
                Join the Pilum Finance community and start exploring a secure,
                user-friendly DeFi ecosystem.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={DEX_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative overflow-hidden inline-flex items-center justify-center rounded-xl px-8 py-4 text-lg font-semibold transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 bg-primary text-text shadow-lg hover:shadow-xl hover:bg-primary-hover hover:scale-105 focus:ring-primary transform border border-primary-hover group"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
                  <span className="flex items-center">
                    Launch DEX Now
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </a>
                <a
                  href={MINER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-xl px-8 py-4 text-lg font-semibold transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 bg-card text-text shadow-md ring-1 ring-border hover:bg-input hover:shadow-lg hover:scale-105 focus:ring-primary transform group"
                >
                  <span className="flex items-center">
                    Start Mining Today
                    <Zap className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform duration-300" />
                  </span>
                </a>
              </div>
            </div>
          </section>

          {/* Contact Us Section */}
          <section id="contact" className="py-16 scroll-mt-24">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl font-bold text-text mb-4">
                  Get In Touch
                </h2>
                <p className="text-text-secondary text-lg max-w-2xl mx-auto">
                  Have questions? Our team is here to help. Send us a message
                  and we'll respond as soon as possible.
                </p>
              </div>

              <ContactForm />
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
